/* ==========================================================================
   NARAK — interacciones
   - Revelado al hacer scroll
   - Onda al hacer clic en botones
   - Cursor "Ver" sobre imágenes de producto
   - Fecha estimada de entrega (3–5 días hábiles)
   - Encabezado con línea al hacer scroll
   ========================================================================== */
(() => {
  const root = document.documentElement;
  root.classList.add('nk-js');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // En escritorio Horizon desplaza .page-wrapper en lugar de la ventana
  const scrollTop = () => {
    const wrapper = document.querySelector('.page-wrapper');
    return Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
  };
  const onScroll = (fn) => {
    document.addEventListener('scroll', fn, { passive: true, capture: true });
  };
  const finePointer = window.matchMedia('(any-pointer: fine)').matches;

  /* ---------- Revelado ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            }
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        )
      : null;

  function observeReveals(scope = document) {
    const targets = scope.querySelectorAll('[data-nk-reveal]:not(.is-in), .nk-split:not(.is-in), .nk-delivery:not(.is-in)');
    targets.forEach((el) => {
      if (!revealObserver || reduceMotion) {
        el.classList.add('is-in');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ---------- Onda al hacer clic ---------- */
  const rippleSelector = '.nk-btn, .button:not(.button-unstyled), .add-to-cart-button, .cart__checkout-button, .nk-tabs__tab';
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (reduceMotion) return;
      const target = event.target.closest(rippleSelector);
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2.2;
      const ripple = document.createElement('span');
      ripple.className = 'nk-ripple';
      ripple.style.width = ripple.style.height = `${size}px`;
      ripple.style.left = `${event.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${event.clientY - rect.top - size / 2}px`;
      if (getComputedStyle(target).position === 'static') target.style.position = 'relative';
      target.style.overflow = 'hidden';
      target.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove());
    },
    { passive: true }
  );

  /* ---------- Cursor "Ver" ---------- */
  function initCursor() {
    if (!finePointer || reduceMotion) return;
    const cursor = document.createElement('div');
    cursor.className = 'nk-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.textContent = 'Ver';
    document.body.appendChild(cursor);

    const zoneSelector = '.product-card .card-gallery, .nk-card__media, [data-nk-cursor]';
    let raf = 0;
    let x = -200;
    let y = -200;

    document.addEventListener(
      'pointermove',
      (event) => {
        x = event.clientX;
        y = event.clientY;
        if (raf) return;
        raf = requestAnimationFrame(() => {
          cursor.style.setProperty('--x', `${x}px`);
          cursor.style.setProperty('--y', `${y}px`);
          raf = 0;
        });
      },
      { passive: true }
    );

    document.addEventListener('pointerover', (event) => {
      const zone = event.target.closest(zoneSelector);
      if (zone) {
        cursor.textContent = zone.getAttribute('data-nk-cursor') || 'Ver';
        cursor.classList.add('is-active');
      }
    });
    document.addEventListener('pointerout', (event) => {
      const zone = event.target.closest(zoneSelector);
      if (zone && !zone.contains(event.relatedTarget)) cursor.classList.remove('is-active');
    });
  }

  /* ---------- Fecha estimada de entrega ---------- */
  function addBusinessDays(start, days) {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return date;
  }

  function formatDate(date) {
    try {
      return new Intl.DateTimeFormat('es-CO', { weekday: 'short', day: 'numeric', month: 'short' })
        .format(date)
        .replace(/\./g, '')
        .replace(/,/g, '');
    } catch (e) {
      return date.toLocaleDateString();
    }
  }

  function renderDeliveryDates(scope = document) {
    scope.querySelectorAll('[data-nk-delivery]').forEach((el) => {
      const min = parseInt(el.getAttribute('data-min') || '3', 10);
      const max = parseInt(el.getAttribute('data-max') || '5', 10);
      const now = new Date();
      const from = addBusinessDays(now, min);
      const to = addBusinessDays(now, max);
      const mode = el.getAttribute('data-nk-delivery');
      let text = `${formatDate(from)} y el ${formatDate(to)}`;
      if (mode === 'from') text = formatDate(from);
      else if (mode === 'to') text = formatDate(to);
      else if (mode === 'today') text = 'Hoy';
      // Solo escribir si cambia: evita bucles con el MutationObserver del carrito
      if (el.textContent !== text) el.textContent = text;
      if (!el.classList.contains('is-ready')) el.classList.add('is-ready');
    });
  }

  /* ---------- Encabezado al hacer scroll ---------- */
  function initHeaderScroll() {
    let ticking = false;
    const update = () => {
      document.body.classList.toggle('nk-scrolled', scrollTop() > 12);
      ticking = false;
    };
    onScroll(() => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    });
    update();
  }

  /* ---------- Pestañas genéricas (Hombre / Mujer) ---------- */
  function initTabs(scope = document) {
    scope.querySelectorAll('[data-nk-tabs]:not([data-nk-ready])').forEach((wrap) => {
      wrap.setAttribute('data-nk-ready', '');
      const tabs = [...wrap.querySelectorAll('[role="tab"]')];
      const panels = [...wrap.querySelectorAll('[role="tabpanel"]')];
      const indicator = wrap.querySelector('.nk-tabs__indicator');

      const moveIndicator = (tab) => {
        if (!indicator || !tab) return;
        indicator.style.width = `${tab.offsetWidth}px`;
        indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
      };

      const select = (tab, focus = false) => {
        tabs.forEach((t) => {
          const selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.id === tab.getAttribute('aria-controls');
          panel.hidden = !active;
          if (active) {
            panel.querySelectorAll('[data-nk-reveal]').forEach((el) => el.classList.remove('is-in'));
            requestAnimationFrame(() => observeReveals(panel));
          }
        });
        moveIndicator(tab);
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const next = tabs[(index + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
          select(next, true);
        });
      });

      const initial = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
      if (initial) requestAnimationFrame(() => moveIndicator(initial));
      window.addEventListener('resize', () => moveIndicator(tabs.find((t) => t.getAttribute('aria-selected') === 'true')));
    });
  }

  /* ---------- Carrusel arrastrable (opiniones) ---------- */
  function initRails(scope = document) {
    scope.querySelectorAll('[data-nk-rail]:not([data-nk-ready])').forEach((wrap) => {
      wrap.setAttribute('data-nk-ready', '');
      const track = wrap.querySelector('[data-nk-rail-track]');
      const prev = wrap.querySelector('[data-nk-rail-prev]');
      const next = wrap.querySelector('[data-nk-rail-next]');
      const progress = wrap.querySelector('[data-nk-rail-progress]');
      if (!track) return;

      const step = () => {
        const item = track.firstElementChild;
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const ratio = max > 0 ? track.scrollLeft / max : 1;
        if (progress) progress.style.transform = `scaleX(${Math.max(0.08, ratio)})`;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
      };
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();

      // Arrastre con mouse
      let down = false;
      let startX = 0;
      let startLeft = 0;
      let moved = false;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true;
        moved = false;
        startX = e.clientX;
        startLeft = track.scrollLeft;
        track.classList.add('is-dragging');
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        down = false;
        track.classList.remove('is-dragging');
      });
      track.addEventListener(
        'click',
        (e) => {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    });
  }

  /* ---------- Parallax suave para imágenes marcadas ---------- */
  function initParallax() {
    if (reduceMotion) return;
    const items = [...document.querySelectorAll('[data-nk-parallax]')];
    if (!items.length) return;
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) continue;
        const speed = parseFloat(el.getAttribute('data-nk-parallax') || '0.08');
        const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      }
      ticking = false;
    };
    onScroll(() => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    });
    update();
  }

  /* ---------- Categorías del catálogo ----------
     Reutiliza el renderizador de secciones de Horizon (el mismo de los filtros):
     cambia la URL a /collections/<colección>/<etiqueta> y vuelve a pintar solo la sección. */
  function initCategoryBar() {
    const renderCatalog = async (url, { push = true } = {}) => {
      const results = document.querySelector('results-list[section-id]');
      if (!results) {
        window.location.href = url;
        return;
      }
      const sectionId = results.getAttribute('section-id');
      if (push) history.pushState({ nkCategory: true }, '', url);
      results.classList.add('nk-catbar-loading');
      try {
        const { sectionRenderer } = await import('@theme/section-renderer');
        await sectionRenderer.renderSection(sectionId);
      } catch (error) {
        if (error && error.name === 'AbortError') return; // otra categoría interrumpió esta
        window.location.href = url;
        return;
      } finally {
        results.classList.remove('nk-catbar-loading');
      }
      const active = document.querySelector('[data-nk-catbar] [aria-current]');
      const list = active && active.closest('.nk-catbar__list');
      if (list && list.scrollWidth > list.clientWidth) {
        list.scrollTo({ left: active.offsetLeft - 16, behavior: 'smooth' });
      }
      observeReveals();
    };

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-nk-catbar] a[data-nk-cat]');
      if (!link || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();

      // Conservar filtros y orden activos; reiniciar la paginación
      const url = new URL(link.href);
      new URL(window.location.href).searchParams.forEach((value, key) => {
        if (key !== 'page' && !url.searchParams.has(key)) url.searchParams.append(key, value);
      });
      if (url.href === window.location.href) return;

      link.closest('[data-nk-catbar]').querySelectorAll('[aria-current]').forEach((a) => a.removeAttribute('aria-current'));
      link.setAttribute('aria-current', 'page');
      renderCatalog(url.href);
    });

    // Atrás / adelante del navegador entre categorías
    window.addEventListener('popstate', () => {
      if (document.querySelector('[data-nk-catbar]')) renderCatalog(window.location.href, { push: false });
    });
  }

  function initAll(scope = document) {
    observeReveals(scope);
    renderDeliveryDates(scope);
    initTabs(scope);
    initRails(scope);
  }

  const start = () => {
    initAll();
    initCursor();
    initHeaderScroll();
    initParallax();
    initCategoryBar();

    // El carrito de Horizon se vuelve a pintar tras añadir productos: re-aplicar fechas.
    const drawerObserver = new MutationObserver(() => {
      renderDeliveryDates();
      observeReveals();
    });
    const cartRoots = document.querySelectorAll('cart-drawer-component, .cart-drawer, .cart-page');
    cartRoots.forEach((el) => drawerObserver.observe(el, { childList: true, subtree: true }));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Editor de temas: re-inicializar secciones al editarlas
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
